import React, { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import getApiUrl from "../utils/api";
import ModernDropdown from "./ModernDropdown";

const AddProjectModal = ({ open, onClose, onAddProject }) => {
  const [projectName, setProjectName] = useState("");
  const [department_id, setDepartmentId] = useState("");
  const [description, setDescription] = useState("");
  const [departments, setDepartments] = useState([]);
  const modalRef = useRef(null);

  // Fetch departments from backend
  useEffect(() => {
    if (!open) return;
    const fetchDepartments = async () => {
      try {
        const res = await fetch(getApiUrl("api/auth/departments"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        const data = await res.json();
        setDepartments(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching departments:", err);
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, [open]);

  // Animate bottom-to-up on open/close
  useEffect(() => {
    if (open && modalRef.current) {
      gsap.fromTo(
        modalRef.current,
        { y: 120, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.38, ease: "power3.out" }
      );
    }
  }, [open]);

  const handleClose = () => {
    if (modalRef.current) {
      gsap.to(modalRef.current, {
        y: 120,
        opacity: 0,
        duration: 0.22,
        ease: "power2.in",
        onComplete: onClose,
      });
    } else {
      onClose();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!projectName) return;
    if (onAddProject)
      onAddProject({
        name: projectName,
        department_id: department_id || null,
        description: description || null,
      });
    setProjectName("");

    setDepartmentId("");
    setDescription("");
    handleClose();
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        ref={modalRef}
        style={{
          background: "#fff",
          borderRadius: 24,
          width: 520,
          maxWidth: "95vw",
          padding: 0,
          boxShadow:
            "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <button
          onClick={handleClose}
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            background: "rgba(255, 255, 255, 0.9)",
            border: "none",
            fontSize: 20,
            cursor: "pointer",
            color: "#374151",
            transition: "all 0.2s ease",
            borderRadius: "50%",
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            backdropFilter: "blur(10px)",
            zIndex: 10,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.color = "#6c757d";
            e.currentTarget.style.background = "rgba(255, 255, 255, 1)";
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.color = "#374151";
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
          }}
        >
          ×
        </button>
        <div
          style={{
            background: "linear-gradient(135deg, #6c757d 0%, #868e96 100%)",
            padding: "32px 32px 24px 32px",
            color: "white",
            textAlign: "center",
            position: "relative",
          }}
        >
          <h2
            style={{
              textAlign: "center",
              margin: 0,
              fontWeight: 700,
              fontSize: "1.5rem",
              letterSpacing: "0.02em",
              textShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            Add Project
          </h2>
          <div
            style={{
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.9)",
              marginBottom: 0,
              marginTop: 8,
              fontSize: "0.9rem",
              fontWeight: 400,
            }}
          >
            Add the required details for the New Project
          </div>
        </div>
        <div style={{ padding: "32px", background: "#fafbfc" }}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 500 }}>
                Add Project <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Add Project Name"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 6,
                  border: "1px solid #ccc",
                  marginTop: 6,
                  fontSize: 16,
                }}
                required
              />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 500 }}>Department</label>
              <ModernDropdown
                name="department_id"
                options={departments}
                value={department_id}
                onChange={(e) => setDepartmentId(e.target.value)}
                placeholder={
                  departments.length > 0
                    ? "Select Department"
                    : "Loading departments..."
                }
                disabled={departments.length === 0}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontWeight: 500 }}>Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add Description"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 6,
                  border: "1px solid #ccc",
                  marginTop: 6,
                  fontSize: 16,
                  minHeight: 80,
                }}
              />
            </div>
            <button
              type="submit"
              style={{
                width: "100%",
                background: "#6c757d",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "12px 0",
                fontWeight: 600,
                fontSize: 16,
                cursor: "pointer",
              }}
            >
              Add Project
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProjectModal;
